/* eslint react/require-default-props: 0 */
import { Box, IconButton, Stack, TextField, Typography, useTheme } from '@mui/material';
import React, { useState, useRef, useEffect, useCallback } from 'react';

import { getMaskedEmailForProp } from '../../utils/helper';
import ButtonComponent from '../button/ButtonComponent';
import { LinkTextWrapper } from '../global-styles/GlobalStyles';
import { colors } from '../theme/theme';
import { BrandLogo } from '../ui-components/BrandLogo';

import { VerifyOtpStyleWrapper } from './otpInput-style';

type ChangeHandler = (otpValue: string, length: number) => void;

const ForgotPasswordLogoSection: React.FC = () => (
    <Stack sx={{ mb: 3, mt: 2 }}>
        <BrandLogo alt="Auxcap logo" style={{ height: '100%', width: '100%', maxHeight: 60, maxWidth: 188 }} />
    </Stack>
);

// Shared "too many attempts" warning shown above the Verify button(s) in both the normal and
// forgot-password layouts.
const MaxAttemptsWarning: React.FC = () => (
    <Typography
        variant="body2"
        sx={{
            textAlign: 'center',
            mb: 2,
            color: colors.errorPreset[10],
            fontWeight: 500,
        }}
    >
        Maximum verification attempts (3) exceeded. Please request a new Passcode.
    </Typography>
);

interface IProps {
    length?: number;
    autoFocus?: boolean;
    type?: string;
    placeholder?: string[];
    showText?: boolean;
    isValidOtp?: boolean;
    onChange: ChangeHandler;
    mainTitle?: string;
    forgotPasswordMainTitle?: string;
    forgotPasswordSubtitle?: string | React.ReactNode;
    subtitle?: string | React.ReactNode;
    subtitle1?: string;
    email?: string;
    onVerify?: (otp: string) => void;
    onClose?: () => void;
    onResend?: () => void;
    verifyButtonText?: string;
    resendText?: string;
    resendTextWithTimer?: string;
    forgotPassword?: boolean;
    forgotPasswordresendTextWithTimer?: string;
    timerCount?: number;
    isCalledFromLogin?: boolean;
    isVerifyDisabled?: boolean;
    attemptCount?: number;
}

export const OtpInput: React.FC<IProps> = ({
    length = 6,
    onChange,
    autoFocus = true,
    type = 'text',
    placeholder = ['', '', '', '', '', ''],
    showText = true,
    isValidOtp = true,
    mainTitle,
    subtitle,
    subtitle1 = '',
    onVerify,
    onClose,
    onResend,
    verifyButtonText = 'Verify',
    resendText = 'Resend Passcode',
    resendTextWithTimer,
    forgotPassword,
    forgotPasswordMainTitle,
    forgotPasswordSubtitle,
    timerCount = 0,
    isVerifyDisabled = false,
    attemptCount = 0,
}) => {
    const [otp, setOtp] = useState(Array(length).fill(''));
    const inputRefs = useRef<Array<HTMLInputElement | null>>(Array.from({ length }, () => null));
    const onVerifyRef = useRef(onVerify);
    /** Prevents duplicate auto-submit for the same passcode string (e.g. React Strict Mode). */
    const autoSubmittedOtpRef = useRef<string>('');
    const [currentTimer, setCurrentTimer] = useState(timerCount);
    const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
    // Track if 30 seconds have passed
    const [showResendOnly, setShowResendOnly] = useState(false);

    useEffect(() => {
        onVerifyRef.current = onVerify;
    });

    const otpValue = otp.join('');
    const theme = useTheme();

    // Helper function to convert email strings to lowercase
    const getLowercaseSubtitle = (subtitleValue: string | React.ReactNode): string | React.ReactNode => {
        if (typeof subtitleValue === 'string') {
            // Convert to lowercase if it's a string
            return subtitleValue.toLowerCase();
        }
        return subtitleValue;
    };

    // Convert subtitle1 to lowercase before processing
    const lowercaseSubtitle1 = subtitle1 ? subtitle1.toLowerCase() : '';
    useEffect(() => {
        if (inputRefs.current[0] && autoFocus) {
            inputRefs.current[0].focus();
        }
    }, [autoFocus]);

    useEffect(() => {
        if (!isValidOtp) {
            const t = setTimeout(() => setOtp(Array(length).fill('')), 0);
            return () => clearTimeout(t);
        }
        return undefined;
    }, [isValidOtp, length]);

    const verifyBlocked = isVerifyDisabled || (attemptCount !== undefined && attemptCount >= 3);

    const tryInvokeVerify = useCallback(
        (value: string, fromAutoSubmit: boolean) => {
            const verify = onVerifyRef.current;
            if (!verify || value.length !== length || verifyBlocked) {
                return;
            }
            if (fromAutoSubmit && autoSubmittedOtpRef.current === value) {
                return;
            }
            if (fromAutoSubmit) {
                autoSubmittedOtpRef.current = value;
            }
            verify(value);
        },
        [length, verifyBlocked],
    );

    // Auto-submit when all digits are entered (no Verify click required); applies everywhere OtpInput is used.
    useEffect(() => {
        if (otpValue.length < length) {
            autoSubmittedOtpRef.current = '';
            return;
        }
        tryInvokeVerify(otpValue, true);
    }, [otpValue, length, verifyBlocked, tryInvokeVerify]);

    // Timer countdown effect
    useEffect(() => {
        // Clear any existing timer
        if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
        }

        const resetT = setTimeout(() => setShowResendOnly(false), 0);
        const initT = setTimeout(() => setCurrentTimer(timerCount > 0 ? timerCount : 0), 0);

        if (timerCount > 0) {
            timerIntervalRef.current = setInterval(() => {
                setCurrentTimer(prev => {
                    if (prev <= 1) {
                        if (timerIntervalRef.current) {
                            clearInterval(timerIntervalRef.current);
                            timerIntervalRef.current = null;
                        }
                        return 0;
                    }
                    // If 30 seconds have passed, show only the resend link
                    if (prev - 1 === timerCount - 30) {
                        setShowResendOnly(true);
                    }
                    return prev - 1;
                });
            }, 1000);
        }

        return () => {
            clearTimeout(resetT);
            clearTimeout(initT);
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
            }
        };
    }, [timerCount]);

    const handleInputChange = (value: string, index: number) => {
        const sanitizedValue = value.replace(/\D/g, '');
        const newOtp = [...otp];
        newOtp[index] = sanitizedValue;
        setOtp(newOtp);
        onChange(newOtp.join(''), length);

        if (sanitizedValue !== '' && index < length - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === 'Backspace' && index > 0 && otp[index] === '') {
            inputRefs.current[index - 1]?.focus();
        }
    };

    // Renders the row of single-digit passcode boxes; identical for both the normal and
    // forgot-password layouts (only the surrounding wrapper differs between the two).
    const renderOtpDigits = () =>
        otp.map((digit, index) => (
            <Box className="otpBox" key={`otp-box-${index}`}>
                <TextField
                    placeholder={placeholder[index]}
                    key={`otp-input-${index}`}
                    type={showText ? 'text' : type}
                    value={digit}
                    onChange={e => handleInputChange(e.target.value, index)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(e, index)}
                    inputRef={ref => (inputRefs.current[index] = ref)}
                    onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => handlePaste(e, index)}
                    variant="outlined"
                    autoComplete="off"
                    inputProps={{
                        maxLength: 1,
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        style: { textAlign: 'center' },
                        'aria-label': `Digit ${index + 1} of ${length}`,
                    }}
                />
            </Box>
        ));

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>, index: number) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text/plain');
        // Strip anything non-numeric (e.g. "123456 is your code", trailing whitespace/newlines)
        // instead of rejecting the whole paste when it isn't pure digits.
        const digitsOnly = pastedData.replace(/\D/g, '');
        if (!digitsOnly) {
            return; // Nothing numeric to paste
        }
        const pastedValues = digitsOnly.split('').slice(0, length - index);
        const newOtp = [...otp];
        pastedValues.forEach((value, i) => {
            newOtp[index + i] = value;
        });
        setOtp(newOtp);
        onChange(newOtp.join(''), length);
    };

    return (
        <VerifyOtpStyleWrapper>
            {!forgotPassword && (
                <>
                    {/* Header */}
                    <Stack className="cardHeader" direction="row">
                        <Typography variant="h5" component="h1" className="mainTitle" id="otp-dialog-title">
                            {mainTitle}
                        </Typography>
                        {onClose && (
                            <IconButton onClick={onClose} aria-label="Close">
                                <span className="icon-close" style={{ color: theme.palette.text.primary }} />
                            </IconButton>
                        )}
                    </Stack>

                    {/* Subtitle or Info */}
                    {subtitle && (
                        <>
                            <Typography variant="body1" className="subTitle" id="otp-dialog-description">
                                {getLowercaseSubtitle(subtitle)}
                            </Typography>
                        </>
                    )}
                    {subtitle1 && (
                        <>
                            <Typography variant="body1" className="subTitle">
                                {getMaskedEmailForProp(lowercaseSubtitle1)}
                            </Typography>
                        </>
                    )}
                    {/* Passcode input boxes */}
                    <Box className="otpContainer">{renderOtpDigits()}</Box>

                    {/* Show timer and label for first 30 seconds, then hide and show only resend link */}
                    {currentTimer > 0 && !showResendOnly && (
                        <Typography
                            variant="body1"
                            sx={{
                                textAlign: 'center',
                                mb: 3,
                                color: colors.greyPreset[80],
                            }}
                        >
                            {resendTextWithTimer}
                        </Typography>
                    )}
                    {(currentTimer === 0 || showResendOnly) && (
                        <Box sx={{ textAlign: 'center', mb: 3 }}>
                            <LinkTextWrapper
                                onClick={
                                    currentTimer === 0
                                        ? () => {
                                              setOtp(Array(length).fill(''));
                                              onChange('', length);
                                              onResend?.();
                                              setTimeout(() => inputRefs.current[0]?.focus(), 0);
                                          }
                                        : undefined
                                }
                                sx={{
                                    ...theme.typography.body1,
                                    pointerEvents: currentTimer === 0 ? 'auto' : 'none',
                                    color: currentTimer === 0 ? colors.primaryLight[100] : colors.greyPreset[60],
                                    opacity: currentTimer === 0 ? 1 : 0.5,
                                }}
                            >
                                {resendText}
                            </LinkTextWrapper>
                        </Box>
                    )}
                    {onVerify && (
                        <>
                            {isVerifyDisabled && <MaxAttemptsWarning />}
                            <ButtonComponent
                                variant="contained"
                                onClick={() => tryInvokeVerify(otpValue, false)}
                                disabled={
                                    otpValue.length !== length ||
                                    isVerifyDisabled ||
                                    (attemptCount !== undefined && attemptCount >= 3)
                                }
                            >
                                {verifyButtonText}
                            </ButtonComponent>
                        </>
                    )}
                </>
            )}
            {forgotPassword && (
                <>
                    <Stack className="cardHeader" mb={'24px !important'}>
                        {<ForgotPasswordLogoSection />}
                        <Typography variant="h5" className="mainTitle" id="otp-dialog-title">
                            {forgotPasswordMainTitle}
                        </Typography>
                        <Typography variant="body1" className="forgotPasswordSubTitle" id="otp-dialog-description">
                            {getLowercaseSubtitle(forgotPasswordSubtitle)}
                        </Typography>
                    </Stack>
                    <Box className="otpContainer" mb={'24px !important'}>
                        {renderOtpDigits()}
                    </Box>
                    {onVerify && (
                        <>
                            {isVerifyDisabled && <MaxAttemptsWarning />}
                            <Stack direction={'row'} gap={3} mb={6}>
                                <ButtonComponent variant="contained" onClick={onClose}>
                                    {'Back'}
                                </ButtonComponent>
                                <ButtonComponent
                                    variant="contained"
                                    onClick={() => tryInvokeVerify(otpValue, false)}
                                    disabled={
                                        otpValue.length !== length ||
                                        isVerifyDisabled ||
                                        (attemptCount !== undefined && attemptCount >= 3)
                                    }
                                >
                                    {verifyButtonText}
                                </ButtonComponent>
                            </Stack>
                        </>
                    )}
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                        <Typography variant="body1" sx={{ color: colors.greyPreset[80] }}>
                            Did not receive code?{' '}
                            <LinkTextWrapper
                                onClick={() => {
                                    setOtp(Array(length).fill(''));
                                    onChange('', length);
                                    onResend?.();
                                    setTimeout(() => inputRefs.current[0]?.focus(), 0);
                                }}
                                sx={{
                                    ...theme.typography.body1,
                                }}
                            >
                                {resendText}
                            </LinkTextWrapper>
                        </Typography>
                    </Box>
                </>
            )}
        </VerifyOtpStyleWrapper>
    );
};
